import React, {Component} from 'react';
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Button,
} from 'reactstrap';
import ReactTable from 'react-table';
import {withTranslation} from 'react-i18next';
import {Colxx} from '../components/common/CustomBootstrap';
import {reactTableText} from '../constants/configs';
import {keyBy, mapValues, omit, capitalize, values} from 'lodash';
import moment from 'moment';
import Api from '../services/Api';
import {
  i18nizeDate,
  getDatePickerProps,
  numberWithCommas,
  getSideColor,
  fixedToWithoutRound,
} from '../helpers/Utils';
import {store} from '../AppRenderer';
import {createMuiTheme, ThemeProvider} from '@material-ui/core/styles';
import {DateTimePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import JalaliUtils from '@date-io/jalaali';
import MomentUtils from '@date-io/moment';
import i18n from '../services/i18n';
import fa_IR from "../constants/locales";

class Trades extends Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 0,
      trades: [],
      loading: false,
      selectedStartDate: null,
      selectedEndDate: null,
    };
    this.pairs = keyBy(store.getState().general.pairs, 'name');
  }

  fetchData = async state => {
    try {
      this.setState({loading: true});
      const offset = state.page * state.pageSize;
      const {data} = await Api.invoke(
        'GET',
        '/admin-ui/user-trades',
        null,
        {
          params: {
            userId: this.props.userId,
            offset,
            limit: state.pageSize,
            ...(state.filtered.length ? omit(mapValues(keyBy(state.filtered, 'id'), 'value'), 'timestamp') : null),
            ...(this.state.selectedStartDate ? {createdFrom: moment(this.state.selectedStartDate).toISOString()} : null),
            ...(this.state.selectedEndDate ? {createdTo: moment(this.state.selectedEndDate).toISOString()} : null),
          }
        },
      );
      this.setState({
        trades: data,
      });
    } catch (e) {
      console.log('error: ', e);
    } finally {
      this.setState({
        loading: false,
      });
    }
  };

  handleDateChange = (type, date) => {
    this.setState({
      [type]: date,
    });
  };

  getColumns = t => [
    {
      Header: t('Pair'),
      accessor: 'pair',
      filterable: true,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          {values(this.pairs).map(pair => (
            <option key={pair.name} value={pair.name}>{pair.localeName}</option>
          ))}
        </select>
      ),
      Cell: props => <span>{this.pairs[props.value] ? this.pairs[props.value].localeName : props.value}</span>,
    },
    {
      Header: t('Side'),
      accessor: 'side',
      filterable: true,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          <option value="BUY">{t('Buy')}</option>
          <option value="SELL">{t('Sell')}</option>
        </select>
      ),
      Cell: props => <span className={getSideColor(props.value)}>{t(capitalize(props.value))}</span>,
    },
    {
      Header: t('Price'),
      accessor: 'price',
      Cell: props => <span>{numberWithCommas(Number(props.value))}</span>
    },
    {
      Header: t('Amount'),
      accessor: 'amount',
      Cell: props => <span>{numberWithCommas(Number(props.value))}</span>,
    },
    {
      Header: t('Fee'),
      accessor: 'fee',
      Cell: props => {
        const {side, pair} = props.original;
        const name = this.pairs[pair] ? this.pairs[pair].localeName : pair;
        const tokens = name.split('/');
        return (
          <span>
              {numberWithCommas(Number(props.value))}
            <span> {side === 'BUY' ? tokens[0] : tokens[1]}</span>
          </span>
        );
      },
      minWidth: 160,
    },
    {
      Header: t('Total'),
      Cell: props => {
        const {cost, pair} = props.original;
        return (
          <span>{fixedToWithoutRound(Number(cost), this.pairs[pair] ? this.pairs[pair].priceDecimals : undefined)}</span>
        );
      },
    },
    {
      Header: t('Date'),
      accessor: 'timestamp',
      Cell: props => <span>{i18nizeDate(props.value)}</span>,
      minWidth: 120,
      filterable: true,
      Filter: ({onChange}) => (
        <ThemeProvider theme={createMuiTheme({
          typography: {
            fontFamily: 'IRANSans',
          },
        })}>
          <MuiPickersUtilsProvider
            utils={i18n.language === fa_IR ? JalaliUtils : MomentUtils}
            locale={i18n.language}
          >
            <DateTimePicker
              {...getDatePickerProps()}
              value={this.state.selectedStartDate}
              TextFieldComponent={props => <input style={{width: '100%'}} placeholder={t('start date')} {...props} />}
              onChange={date => {
                this.handleDateChange('selectedStartDate', date);
                onChange(date);
              }}
            />
            <div style={{height: 4}} />
            <DateTimePicker
              {...getDatePickerProps()}
              value={this.state.selectedEndDate}
              TextFieldComponent={props => <input style={{width: '100%'}} placeholder={t('end date')} {...props} />}
              onChange={date => {
                this.handleDateChange('selectedEndDate', date);
                onChange(date);
              }}
            />
          </MuiPickersUtilsProvider>
        </ThemeProvider>
      ),
    },
  ];

  renderNextComponent = () => {
    return (
      <div className='-next'>
        <Button
          className='-btn'
          style={{height: '40px'}}
          disabled={!this.table || this.state.trades.length < this.table.state.pageSize}
          onClick={() => {
            this.setState({
              page: this.state.page + 1
            }, async () => {
              await this.fetchData({
                ...this.table.state,
                page: this.state.page,
              });
            });
          }}
        >
          {this.props.t('Next Page')}
        </Button>
      </div>
    );
  };

  renderPreviousComponent = () => {
    return (
      <div className="-previous">
        <Button
          className='-btn'
          style={{height: '40px'}}
          disabled={!this.table || this.state.page === 0}
          onClick={() => {
            this.setState({
              page: this.state.page - 1,
            }, async () => {
              await this.fetchData({
                ...this.table.state,
                page: this.state.page,
              });
            });
          }}
        >
          {this.props.t('Previous Page')}
        </Button>
      </div>
    );
  };

  render() {
    const {t} = this.props;
    return (
      <Row>
        <Colxx xxs="12" className="mb-4">
          <Card className="h-100">
            <CardBody>
              <CardTitle className="text-large text-primary">
                {t('Trades')}
              </CardTitle>
              <ReactTable
                manual
                ref={el => {this.table = el;}}
                className="-striped -highlight user-trades-table"
                data={this.state.trades}
                loading={this.state.loading}
                sortable={false}
                resizable={true}
                showPageJump={false}
                onFetchData={this.fetchData}
                columns={this.getColumns(t)}
                defaultPageSize={10}
                style={{
                  width: '100%'
                }}
                {...reactTableText}
                NextComponent={this.renderNextComponent}
                PreviousComponent={this.renderPreviousComponent}
                onFilteredChange={() => {this.setState({page: 0});}}
              />
            </CardBody>
          </Card>
        </Colxx>
      </Row>
    );
  }
}

export default withTranslation()(Trades);
